/**
 * snap.js — mode-aware, primitive-aware snap engine
 *
 * Priority order (first match wins):
 *   1. Snap lock (previously locked point)
 *   2. Node endpoint
 *   3. Circle center
 *   4. Arc center / endpoint / midpoint
 *   5. Line intersection
 *   6. Circle quadrant (0°, 90°, 180°, 270°)
 *   7. Line edge / midpoint
 *   8. Circle nearest point (circumference)
 *   9. Arc nearest point
 *  10. Grid snap (mode-dependent precision)
 */

import { SNAP_LOCK } from "../constants";
import { Geo } from "../utils/geo";
import { intP } from "../utils/coords";
import { getMode } from "../constants/drawingModes";
import {
  circleSnapPoints, arcSnapPoints,
  snapToCircle, snapToArc,
} from "./primitives";

function roundTo(v, snap) {
  return Math.round(v / snap) * snap;
}

function snapToGrid(p, snap) {
  return { x: roundTo(p.x, snap), y: roundTo(p.y, snap) };
}

export function computeSnap(rawMm, st, grid, locked) {
  const mode = getMode(st);
  const {
    snapNode: SNAP_NODE,
    snapEdge: SNAP_EDGE,
    snapMid:  SNAP_MID,
    snapIx:   SNAP_IX,
    worldSnap: WORLD_SNAP,
  } = mode;

  // 1. Snap lock
  if (locked && Geo.d(rawMm, locked.point) < SNAP_LOCK * (WORLD_SNAP / 100)) {
    return { ...locked, locked: true };
  }

  // 2. Node snap (line endpoints)
  let bestNode = null, bestND = SNAP_NODE * SNAP_NODE;
  for (const n of Object.values(st.nodes || {})) {
    const d = Geo.dsq(rawMm, n);
    if (d < bestND) { bestND = d; bestNode = n; }
  }
  if (bestNode) return { point: { x: bestNode.x, y: bestNode.y }, type: "node", nodeId: bestNode.id, locked: false };

  // 3. Circle center snap
  for (const c of Object.values(st.circles || {})) {
    const d = Geo.dsq(rawMm, { x: c.cx, y: c.cy });
    if (d < SNAP_NODE * SNAP_NODE) {
      return { point: { x: c.cx, y: c.cy }, type: "center", circleId: c.id, locked: false };
    }
  }

  // 4. Arc special points (center, endpoints, midpoint)
  for (const a of Object.values(st.arcs || {})) {
    for (const sp of arcSnapPoints(a)) {
      const d = Geo.dsq(rawMm, sp);
      if (d < SNAP_NODE * SNAP_NODE) {
        return { point: { x: sp.x, y: sp.y }, type: sp.type, arcId: a.id, locked: false };
      }
    }
  }

  // 5. Line-line intersection
  const pad = Math.max(SNAP_IX, SNAP_EDGE) * 2;
  const nearby = grid
    ? [...grid.query(rawMm.x - pad, rawMm.y - pad, rawMm.x + pad, rawMm.y + pad)]
    : Object.keys(st.edges || {});

  const cands = nearby.map(eId => {
    const e = (st.edges || {})[eId]; if (!e) return null;
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (!ab || !ba) return null;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) return null;
    return { eId, nA, nB };
  }).filter(Boolean);

  let bestIx = null, bestIxD = SNAP_IX * SNAP_IX;
  for (let i = 0; i < cands.length; i++) {
    for (let j = i + 1; j < cands.length; j++) {
      const ix = Geo.sx(cands[i].nA, cands[i].nB, cands[j].nA, cands[j].nB);
      if (!ix) continue;
      const d = Geo.dsq(rawMm, ix);
      if (d < bestIxD) { bestIxD = d; bestIx = ix; }
    }
  }
  if (bestIx) return { point: { x: roundTo(bestIx.x, 0.001), y: roundTo(bestIx.y, 0.001) }, type: "intersection", locked: false };

  // 6. Circle quadrant snap
  for (const c of Object.values(st.circles || {})) {
    for (const sp of circleSnapPoints(c)) {
      if (sp.type !== "quadrant") continue;
      const d = Geo.dsq(rawMm, sp);
      if (d < SNAP_EDGE * SNAP_EDGE) {
        return { point: { x: sp.x, y: sp.y }, type: "quadrant", circleId: c.id, locked: false };
      }
    }
  }

  // 7. Line edge / midpoint snap
  let bestEdge = null, bestED = SNAP_EDGE * SNAP_EDGE, bestEPt = null, bestEType = "edge";
  for (const { eId, nA, nB } of cands) {
    const mid = { x: (nA.x + nB.x) / 2, y: (nA.y + nB.y) / 2 };
    const dM = Geo.dsq(rawMm, mid);
    if (dM < SNAP_MID * SNAP_MID && dM < bestED) {
      bestED = dM; bestEdge = eId;
      bestEPt = mid; bestEType = "midpoint";
    }
    const pr = Geo.project(rawMm, nA, nB);
    const dP = pr.dist * pr.dist;
    if (dP < SNAP_EDGE * SNAP_EDGE && dP < bestED) {
      bestED = dP; bestEdge = eId;
      bestEPt = pr.point; bestEType = "edge";
    }
  }
  if (bestEdge) return { point: intP(bestEPt), type: bestEType, edgeId: bestEdge, locked: false };

  // 8. Circle nearest circumference point
  let bestCircle = null, bestCD = SNAP_EDGE * SNAP_EDGE, bestCPt = null;
  for (const c of Object.values(st.circles || {})) {
    const nearest = snapToCircle(rawMm, c);
    const d = Geo.dsq(rawMm, nearest);
    if (d < bestCD) { bestCD = d; bestCircle = c.id; bestCPt = nearest; }
  }
  if (bestCircle) return { point: bestCPt, type: "edge", circleId: bestCircle, locked: false };

  // 9. Arc nearest point
  let bestArc = null, bestAD = SNAP_EDGE * SNAP_EDGE, bestAPt = null;
  for (const a of Object.values(st.arcs || {})) {
    const nearest = snapToArc(rawMm, a);
    const d = Geo.dsq(rawMm, nearest);
    if (d < bestAD) { bestAD = d; bestArc = a.id; bestAPt = nearest; }
  }
  if (bestArc) return { point: bestAPt, type: "edge", arcId: bestArc, locked: false };

  // 10. Grid snap — mode-dependent precision
  const snapped = snapToGrid(rawMm, WORLD_SNAP);
  return { point: snapped, type: "grid", locked: false };
}
